import type {TFunction} from 'i18next';

import type {
    ActivityChatMessage,
    ChatActivityMessageParticipants,
    ChatActivityType,
} from '../types';

const toDisplayName = (
    {displayName, isMe}: ChatActivityMessageParticipants,
    t: TFunction,
) =>
    isMe
        ? t('meeting.name-with-you', {
              defaultValue: '{{displayName}} (You)',
              displayName,
          })
        : displayName;

const toGroupedText = (
    groupedParticipants: ChatActivityMessageParticipants[],
    chatActivityType: ChatActivityType,
    t: TFunction,
) => {
    const name = (index: number) => {
        const participant = groupedParticipants.at(index);
        return participant ? toDisplayName(participant, t) : undefined;
    };

    switch (groupedParticipants.length) {
        case 2: {
            switch (chatActivityType) {
                case 'joined':
                    return t('meeting.two-participants-joined-group', {
                        defaultValue: '{{second}} and {{first}} joined',
                        first: name(0),
                        second: name(1),
                    });
                case 'left':
                    return t('meeting.two-participants-left-group', {
                        defaultValue: '{{second}} and {{first}} left',
                        first: name(0),
                        second: name(1),
                    });
                default:
                    return undefined;
            }
        }
        case 3: {
            switch (chatActivityType) {
                case 'joined':
                    return t('meeting.three-participants-joined-group', {
                        defaultValue:
                            '{{third}}, {{second}} and {{first}} joined',
                        first: name(0),
                        second: name(1),
                        third: name(2),
                    });
                case 'left':
                    return t('meeting.three-participants-left-group', {
                        defaultValue:
                            '{{third}}, {{second}} and {{first}} left',
                        first: name(0),
                        second: name(1),
                        third: name(2),
                    });
                default:
                    return undefined;
            }
        }
        case 4: {
            switch (chatActivityType) {
                case 'joined':
                    return t('meeting.four-participants-joined-group', {
                        defaultValue:
                            '{{third}}, {{second}}, {{first}} and 1 other joined',
                        first: name(1),
                        second: name(2),
                        third: name(3),
                    });
                case 'left':
                    return t('meeting.four-participants-left-group', {
                        defaultValue:
                            '{{third}}, {{second}}, {{first}} and 1 other left',
                        first: name(1),
                        second: name(2),
                        third: name(3),
                    });
                default:
                    return undefined;
            }
        }
        default: {
            switch (chatActivityType) {
                case 'joined':
                    return t(
                        'meeting.more-than-four-participants-joined-group',
                        {
                            defaultValue:
                                '{{third}}, {{second}}, {{first}} and {{remaining}} others joined',
                            first: name(-3),
                            second: name(-2),
                            third: name(-1),
                            remaining: groupedParticipants.length - 3,
                        },
                    );
                case 'left':
                    return t('meeting.more-than-four-participants-left-group', {
                        defaultValue:
                            '{{third}}, {{second}}, {{first}} and {{remaining}} others left',
                        first: name(-3),
                        second: name(-2),
                        third: name(-1),
                        remaining: groupedParticipants.length - 3,
                    });
                default:
                    return undefined;
            }
        }
    }
};

const toSingleText = (
    message: ActivityChatMessage,
    t: TFunction,
): string | undefined => {
    const displayName = toDisplayName(message, t);

    switch (message.activityType) {
        case 'joined':
            return t('meeting.participant-joined', {
                defaultValue: '{{displayName}} joined',
                displayName,
            });
        case 'left':
            return t('meeting.participant-left', {
                defaultValue: '{{displayName}} left',
                displayName,
            });
        case 'transferred':
            return t('meeting.participant-transfer', {
                defaultValue: '{{displayName}} transferred',
                displayName,
            });
        case 'removed':
            return t('meeting.participant-removed', {
                defaultValue: '{{displayName}} removed',
                displayName,
            });
        case 'empty-chat':
            return t('meeting.empty-chat', {
                defaultValue:
                    'Looks like nobody posted chat messages while you were in the room',
            });
        default:
            return undefined;
    }
};

/**
 * Builds the text shown for an activity message.
 *
 * The text is derived rather than stored, so that it follows the currently
 * selected language instead of the one in use when the activity happened.
 */
export const toActivityMessageText = (
    message: ActivityChatMessage,
    t: TFunction,
): string => {
    const {groupedParticipants, activityType} = message;

    if (groupedParticipants && groupedParticipants.length > 1 && activityType) {
        return toGroupedText(groupedParticipants, activityType, t) ?? '';
    }

    return toSingleText(message, t) ?? '';
};
